import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, Stepper } from '../shared';
import { formatNum } from '../../utils/helpers';

const STORAGE_KEY = 'skill_enchant_calc_last';

// Skill enchant base chances (interlude-online specific)
const SKILL_BASE_CHANCES = [
  98, 97, 96, 95,   // +1 to +4
  94, 83, 78, 72,   // +5 to +8
  69, 65, 60, 56,   // +9 to +12
  52, 48, 44, 42,   // +13 to +16
  40, 35, 31, 29,   // +17 to +20
  25, 22, 21, 20,   // +21 to +24 -- need correct data
  20, 20, 19, 16,   // +25 to +28 -- need correct data (except 27 -> 28)
  14, 12            // +29 to +30 -- need correct data
];

// Mastery bonuses and costs (gold dragons)
const MASTERY_DATA = [
  { bonus: 0, dragons: 0, cumulative: 0 },
  { bonus: 3, dragons: 8, cumulative: 8 },
  { bonus: 5, dragons: 60, cumulative: 68 },
  { bonus: 8, dragons: 300, cumulative: 368 }
];

const SAFE_POINTS = [0, 4, 8, 12, 16, 20, 24, 28, 32];

function calculateExpectedBooks(targetLevel, currentLevel, masteryBonus) {
  let expectedBooks = 0;
  
  const startingSafeIdx = SAFE_POINTS.findIndex(sp => sp > currentLevel) - 1;
  const effectiveStart = Math.max(0, startingSafeIdx);
  
  for (let segIdx = effectiveStart; segIdx < SAFE_POINTS.length - 1; segIdx++) {
    const segSafePoint = SAFE_POINTS[segIdx];
    const segStart = Math.max(segSafePoint, currentLevel);
    const segEnd = Math.min(SAFE_POINTS[segIdx + 1], targetLevel);
    
    if (segStart >= targetLevel) break;
    if (segStart >= segEnd) continue;
    
    const isFirstSegment = segIdx === effectiveStart;
    const startingMidSegment = isFirstSegment && currentLevel > segSafePoint;
    
    let segmentProb = 1;
    
    for (let level = segStart; level < segEnd; level++) {
      const baseChance = SKILL_BASE_CHANCES[level] || 50;
      const finalChance = Math.min(100, baseChance * (1 + masteryBonus / 100));
      const p = finalChance / 100;
      segmentProb *= p;
    }
    
    if (startingMidSegment) {
      let fullSegmentProb = 1;
      for (let level = segSafePoint; level < segEnd; level++) {
        const baseChance = SKILL_BASE_CHANCES[level] || 50;
        const finalChance = Math.min(100, baseChance * (1 + masteryBonus / 100));
        const p = finalChance / 100;
        fullSegmentProb *= p;
      }
      
      const probFailPartial = 1 - segmentProb;
      if (fullSegmentProb > 0) {
        expectedBooks += probFailPartial / fullSegmentProb;
      }
    } else {
      const expectedRuns = segmentProb > 0 ? 1 / segmentProb : Infinity;
      expectedBooks += expectedRuns;
    }
  }
  
  return expectedBooks;
}

function calculateExpectedAttempts(targetLevel, currentLevel, masteryBonus) {
  let expectedAttempts = 0;
  
  const startingSafeIdx = SAFE_POINTS.findIndex(sp => sp > currentLevel) - 1;
  const effectiveStart = Math.max(0, startingSafeIdx);
  
  for (let segIdx = effectiveStart; segIdx < SAFE_POINTS.length - 1; segIdx++) {
    const segSafePoint = SAFE_POINTS[segIdx];
    const segStart = Math.max(segSafePoint, currentLevel);
    const segEnd = Math.min(SAFE_POINTS[segIdx + 1], targetLevel);
    
    if (segStart >= targetLevel) break;
    if (segStart >= segEnd) continue;
    
    const isFirstSegment = segIdx === effectiveStart;
    const startingMidSegment = isFirstSegment && currentLevel > segSafePoint;
    
    let segmentProb = 1;
    let cumulativeProduct = 1;
    let sumCumulativeProducts = 0;
    
    for (let level = segStart; level < segEnd; level++) {
      const baseChance = SKILL_BASE_CHANCES[level] || 50;
      const finalChance = Math.min(100, baseChance * (1 + masteryBonus / 100));
      const p = finalChance / 100;
      
      sumCumulativeProducts += cumulativeProduct;
      cumulativeProduct *= p;
      segmentProb *= p;
    }
    
    if (startingMidSegment) {
      let fullSegmentProb = 1;
      let fullCumulativeProduct = 1;
      let fullSumCumulativeProducts = 0;
      
      for (let level = segSafePoint; level < segEnd; level++) {
        const baseChance = SKILL_BASE_CHANCES[level] || 50;
        const finalChance = Math.min(100, baseChance * (1 + masteryBonus / 100));
        const p = finalChance / 100;
        
        fullSumCumulativeProducts += fullCumulativeProduct;
        fullCumulativeProduct *= p;
        fullSegmentProb *= p;
      }
      
      const probFailPartial = 1 - segmentProb;
      
      if (fullSegmentProb > 0) {
        expectedAttempts += sumCumulativeProducts / segmentProb;
        expectedAttempts += probFailPartial * fullSumCumulativeProducts / fullSegmentProb;
      }
    } else {
      if (segmentProb > 0) {
        expectedAttempts += sumCumulativeProducts / segmentProb;
      }
    }
  }
  
  return expectedAttempts;
}

function calculateGuaranteedBooks(targetLevel, currentLevel) {
  let guaranteedBooks = 0;
  
  const startingSafeIdx = SAFE_POINTS.findIndex(sp => sp > currentLevel) - 1;
  const effectiveStart = Math.max(0, startingSafeIdx);
  
  for (let segIdx = effectiveStart; segIdx < SAFE_POINTS.length - 1; segIdx++) {
    const segSafePoint = SAFE_POINTS[segIdx];
    const segStart = Math.max(segSafePoint, currentLevel);
    const segEnd = Math.min(SAFE_POINTS[segIdx + 1], targetLevel);
    
    if (segStart >= targetLevel) break;
    if (segStart >= segEnd) continue;
    
    const segmentLevels = segEnd - segStart;
    const isFirstSegment = segIdx === effectiveStart;
    const startingMidSegment = isFirstSegment && currentLevel > segSafePoint;
    
    if (segmentLevels > 0 && !startingMidSegment) {
      guaranteedBooks++;
    }
  }
  
  return guaranteedBooks;
}

export function SkillPanel({ active }) {
  const { t, lang, getPlural } = useLanguage();
  
  const [targetLevel, setTargetLevel] = useState(30);
  const [hasCurrentLevel, setHasCurrentLevel] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [masteryLevel, setMasteryLevel] = useState(0);
  const [bookPrice, setBookPrice] = useState(2500);
  const [aaPrice, setAaPrice] = useState(20);

  // Restore state
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const state = JSON.parse(saved);
        if (state.targetLevel) setTargetLevel(parseInt(state.targetLevel) || 30);
        if (state.masteryLevel !== undefined) setMasteryLevel(state.masteryLevel);
        if (state.hasCurrentLevel) {
          setHasCurrentLevel(true);
          if (state.currentLevel) setCurrentLevel(parseInt(state.currentLevel) || 0);
        }
        if (state.bookPrice) setBookPrice(parseInt(state.bookPrice) || 2500);
        if (state.aaPrice) setAaPrice(parseInt(state.aaPrice) || 20);
      }
    } catch (e) {}
  }, []);

  // Save state
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        targetLevel,
        masteryLevel,
        hasCurrentLevel,
        currentLevel,
        bookPrice,
        aaPrice
      }));
    } catch (e) {}
  }, [targetLevel, masteryLevel, hasCurrentLevel, currentLevel, bookPrice, aaPrice]);

  // Auto-clamp current level when target changes
  useEffect(() => {
    if (hasCurrentLevel && currentLevel >= targetLevel) {
      setCurrentLevel(targetLevel - 1);
    }
  }, [targetLevel, currentLevel, hasCurrentLevel]);

  // Clamp current level if it exceeds target
  const effectiveCurrentLevel = useMemo(() => {
    if (!hasCurrentLevel) return 0;
    return Math.min(currentLevel, targetLevel - 1);
  }, [hasCurrentLevel, currentLevel, targetLevel]);

  const goldDragonPrice = 25 * aaPrice;
  const masteryBonus = MASTERY_DATA[masteryLevel].bonus;

  // Calculate expected values for all mastery levels
  const expectedBooksForLevel = useMemo(() => {
    return MASTERY_DATA.map((_, lvl) => 
      calculateExpectedBooks(targetLevel, effectiveCurrentLevel, MASTERY_DATA[lvl].bonus)
    );
  }, [targetLevel, effectiveCurrentLevel]);

  const calculations = useMemo(() => {
    const expectedBooks = expectedBooksForLevel[masteryLevel];
    const expectedAttempts = calculateExpectedAttempts(targetLevel, effectiveCurrentLevel, masteryBonus);
    const guaranteedBooks = calculateGuaranteedBooks(targetLevel, effectiveCurrentLevel);
    const expectedBooksCost = expectedBooks * bookPrice;
    
    return { expectedBooks, expectedAttempts, guaranteedBooks, expectedBooksCost };
  }, [targetLevel, effectiveCurrentLevel, masteryBonus, masteryLevel, bookPrice, expectedBooksForLevel]);

  // Mastery comparison calculations
  const masteryComparison = useMemo(() => {
    if (masteryLevel === 0) return null;
    
    const currentExpectedBooks = expectedBooksForLevel[masteryLevel];
    const noMasteryBooks = expectedBooksForLevel[0];
    const booksSaved = noMasteryBooks - currentExpectedBooks;
    const adenaSaved = booksSaved * bookPrice;
    const masteryCost = MASTERY_DATA[masteryLevel].cumulative * goldDragonPrice;
    const breakEvenSkills = adenaSaved > 0 ? Math.ceil(masteryCost / adenaSaved) : Infinity;
    
    // vs Previous level
    let prevComparison = null;
    if (masteryLevel > 1) {
      const prevLevel = masteryLevel - 1;
      const prevExpectedBooks = expectedBooksForLevel[prevLevel];
      const booksSavedVsPrev = prevExpectedBooks - currentExpectedBooks;
      const adenaSavedVsPrev = booksSavedVsPrev * bookPrice;
      const prevMasteryCost = MASTERY_DATA[prevLevel].cumulative * goldDragonPrice;
      const upgradeCost = masteryCost - prevMasteryCost;
      const breakEvenSkillsVsPrev = adenaSavedVsPrev > 0 ? Math.ceil(upgradeCost / adenaSavedVsPrev) : Infinity;
      
      prevComparison = {
        prevLevel,
        breakEvenSkillsVsPrev
      };
    }
    
    return {
      booksSaved,
      adenaSaved,
      masteryCost,
      breakEvenSkills,
      prevComparison
    };
  }, [masteryLevel, expectedBooksForLevel, bookPrice, goldDragonPrice]);

  // Generate chances grid
  const chancesGrid = useMemo(() => {
    return Array.from({ length: 30 }, (_, i) => {
      const level = i + 1;
      const baseChance = SKILL_BASE_CHANCES[i] || 50;
      const finalChance = Math.min(100, baseChance * (1 + masteryBonus / 100));
      const isSafe = level % 4 === 0;
      const needsBook = (level - 1) % 4 === 0;
      const isBelowCurrent = level <= effectiveCurrentLevel;
      
      return { level, finalChance, isSafe, needsBook, isBelowCurrent };
    });
  }, [masteryBonus, effectiveCurrentLevel]);

  const handleCurrentLevelToggle = (e) => {
    setHasCurrentLevel(e.target.checked);
    if (!e.target.checked) {
      setCurrentLevel(0);
    }
  };

  const getSkillWord = (n) => {
    if (lang === 'ru') {
      return getPlural(n, 'скилл', 'скилла', 'скиллов');
    }
    if (lang === 'ua') {
      return getPlural(n, 'скіл', 'скіли', 'скілів');
    }
    return n === 1 ? 'skill' : 'skills';
  };

  if (!active) return null;

  return (
    <div className="calculator-panel active">
      <Card title={t('targetEnchantLevel')}>
        <div className="input-group">
          <Stepper
            value={targetLevel}
            onChange={setTargetLevel}
            min={1}
            max={30}
          />
        </div>

        <div className="safe-levels-buttons">
          {[4, 8, 12, 16, 20, 24, 28].map(lvl => (
            <button
              key={lvl}
              className={`safe-level-btn ${targetLevel === lvl ? 'active' : ''}`}
              onClick={() => setTargetLevel(lvl)}
            >
              +{lvl}
            </button>
          ))}
        </div>

        <div className="current-level-toggle">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={hasCurrentLevel}
              onChange={handleCurrentLevelToggle}
            />
            <span className="checkbox-custom"></span>
            <span className="toggle-text">{t('alreadyEnchanted')}</span>
          </label>
        </div>

        {hasCurrentLevel && (
          <div className="current-level-input">
            <label>{t('currentLevel')}</label>
            <Stepper
              value={currentLevel}
              onChange={(v) => setCurrentLevel(Math.min(v, targetLevel - 1))}
              min={0}
              max={targetLevel - 1}
              small
            />
          </div>
        )}
      </Card>

      <Card title={t('expectedCostSummary', { currentLevel: effectiveCurrentLevel, targetLevel })}>
        <div className="row">
          <span className="row-label">{t('booksGuaranteed')}</span>
          <span className="row-value">{calculations.guaranteedBooks}</span>
        </div>
        <div className="row">
          <span className="row-label">{t('expectedAttempts')}</span>
          <span className="row-value">~{Math.ceil(calculations.expectedAttempts)}</span>
        </div>
        <div className="row">
          <span className="row-label">{t('expectedBooks')}</span>
          <span className="row-value">~{calculations.expectedBooks.toFixed(1)}</span>
        </div>
        <div className="row highlight">
          <span className="row-label">{t('expectedBooksCost')}</span>
          <span className="row-value">~{formatNum(Math.ceil(calculations.expectedBooksCost))}</span>
        </div>

        {masteryComparison && (
          <div className="mastery-comparison">
            <div className="mastery-comp-row">
              <span className="mastery-comp-label">{t('masterySavesPerSkill')}</span>
              <span className="mastery-comp-value">
                ↓{masteryComparison.booksSaved.toFixed(1)} {t('books')} / ↓{formatNum(Math.ceil(masteryComparison.adenaSaved))}
              </span>
            </div>
            <div className="mastery-comp-row">
              <span className="mastery-comp-label">{t('masteryOneTimeCost')}</span>
              <span className="mastery-comp-value mastery-cost">{formatNum(masteryComparison.masteryCost)}</span>
            </div>
            <div className={`mastery-comp-row main ${masteryComparison.breakEvenSkills <= 1 ? 'positive' : masteryComparison.breakEvenSkills > 10 ? 'negative' : ''}`}>
              <span className="mastery-comp-label">{t('masteryBreakEvenVsNone')}</span>
              <span className="mastery-comp-value">
                {masteryComparison.breakEvenSkills === 1
                  ? `✅ ${t('paysOffNow')}`
                  : masteryComparison.breakEvenSkills > 10
                    ? `⚠️ ${masteryComparison.breakEvenSkills} ${getSkillWord(masteryComparison.breakEvenSkills)}`
                    : `${masteryComparison.breakEvenSkills} ${getSkillWord(masteryComparison.breakEvenSkills)}`
                }
              </span>
            </div>
            {masteryComparison.prevComparison && (
              <div className={`mastery-comp-row main ${masteryComparison.prevComparison.breakEvenSkillsVsPrev <= 1 ? 'positive' : masteryComparison.prevComparison.breakEvenSkillsVsPrev > 10 ? 'negative' : ''}`}>
                <span className="mastery-comp-label">
                  {t('masteryBreakEvenVsPrev')} Lv.{masteryComparison.prevComparison.prevLevel}
                </span>
                <span className="mastery-comp-value">
                  {masteryComparison.prevComparison.breakEvenSkillsVsPrev === 1
                    ? `✅ ${t('paysOffNow')}`
                    : masteryComparison.prevComparison.breakEvenSkillsVsPrev > 10
                      ? `⚠️ ${masteryComparison.prevComparison.breakEvenSkillsVsPrev} ${getSkillWord(masteryComparison.prevComparison.breakEvenSkillsVsPrev)}`
                      : `${masteryComparison.prevComparison.breakEvenSkillsVsPrev} ${getSkillWord(masteryComparison.prevComparison.breakEvenSkillsVsPrev)}`
                  }
                </span>
              </div>
            )}
          </div>
        )}

        <div className="mastery-section">
          <div className="mastery-header">{t('skillEnchantMastery')}</div>
          <div className="mastery-selector">
            {MASTERY_DATA.map((data, idx) => (
              <button
                key={idx}
                className={`mastery-btn ${masteryLevel === idx ? 'active' : ''}`}
                onClick={() => setMasteryLevel(idx)}
              >
                <span className="mastery-level">{idx === 0 ? t('masteryNone') : `Lv.${idx}`}</span>
                <span className="mastery-bonus">+{data.bonus}%</span>
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card title={t('priceSettings')}>
        <div className="input-group">
          <label>{t('enchantBookPrice')}</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={bookPrice}
            onChange={(e) => setBookPrice(parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0)}
            onBlur={() => setBookPrice(prev => Math.max(1, prev))}
            onFocus={(e) => {
              const input = e.target;
              requestAnimationFrame(() => input.setSelectionRange(input.value.length, input.value.length));
            }}
          />
        </div>
        <div className="input-group">
          <label>{t('ancientAdenaPrice')}</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={aaPrice}
            onChange={(e) => setAaPrice(parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0)}
            onBlur={() => setAaPrice(prev => Math.max(1, prev))}
            onFocus={(e) => {
              const input = e.target;
              requestAnimationFrame(() => input.setSelectionRange(input.value.length, input.value.length));
            }}
          />
        </div>
        <p className="note" style={{ marginTop: '12px', textAlign: 'left' }}>
          25 AA = 1 Gold Dragon<br />
          Mastery costs: 8 / 60 / 300 Gold Dragons
        </p>
      </Card>

      <Card title={t('enchantChances')}>
        <div className="skill-chances-grid">
          {chancesGrid.map(({ level, finalChance, isSafe, needsBook, isBelowCurrent }) => (
            <div
              key={level}
              className={`skill-chance-item ${isSafe ? 'safe' : ''} ${needsBook ? 'book-needed' : ''} ${isBelowCurrent ? 'below-current' : ''}`}
            >
              <div className="chance-level">+{level}</div>
              <div className="chance-value">{finalChance.toFixed(1)}%</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
